/**
 * Raw design tokens.
 *
 * The neutral scale is a warm stone ramp: neutral greys with a faint warm
 * undertone, matching the reference dashboard's temperature. It is deliberately
 * far less saturated than a cream scale — warmth reads as temperature, never as
 * beige.
 *
 * Brand hues (orange, coral, teal, blue) come from the Outright logo. Orange
 * drives interaction; the rest are semantic only.
 */
export const tokens = {
  colors: {
    /** Brand orange. 400 is the signature fill and carries dark text (6.6:1). */
    orange: {
      50: { value: '#FEF6EC' },
      100: { value: '#FCE7CD' },
      200: { value: '#F8CE9C' },
      300: { value: '#F3AE60' },
      400: { value: '#EC8B1E' },
      500: { value: '#D2760F' },
      600: { value: '#AA5D0D' },
      700: { value: '#88490C' },
      800: { value: '#67380B' },
      900: { value: '#452508' },
      950: { value: '#2A1605' },
    },

    /** Coral — destructive actions and errors. */
    coral: {
      50: { value: '#FEF1EE' },
      100: { value: '#FCDCD5' },
      200: { value: '#F8B9AB' },
      300: { value: '#F39181' },
      400: { value: '#ED6C57' },
      500: { value: '#DE4E36' },
      600: { value: '#BC3D28' },
      700: { value: '#96311F' },
      800: { value: '#6F2517' },
      900: { value: '#4A1810' },
      950: { value: '#2B0E09' },
    },

    /** Teal — success and positive movement. */
    teal: {
      50: { value: '#E9F7F4' },
      100: { value: '#C8EBE4' },
      200: { value: '#97D9CD' },
      300: { value: '#61C2B2' },
      400: { value: '#34A798' },
      500: { value: '#1B8C7E' },
      600: { value: '#147065' },
      700: { value: '#115A52' },
      800: { value: '#0E453F' },
      900: { value: '#0A2E2A' },
      950: { value: '#061917' },
    },

    /** Blue — information and secondary chart series. */
    blue: {
      50: { value: '#EDF3FE' },
      100: { value: '#D5E2FC' },
      200: { value: '#ABC5F8' },
      300: { value: '#7BA3F1' },
      400: { value: '#5081E4' },
      500: { value: '#3563CE' },
      600: { value: '#284EA8' },
      700: { value: '#223F85' },
      800: { value: '#1D3468' },
      900: { value: '#172745' },
      950: { value: '#0D1526' },
    },

    /**
     * Chart-only hues. Two more identities than the brand carries, so a grouped
     * chart can seat six series without repeating one — the brand four run out at
     * four. Not semantic: never used for state, only for "this bar is that person".
     * Both steps are validated against their own surface (see the chart tokens).
     */
    violet: {
      400: { value: '#8B5CF0' },
      500: { value: '#7C4DCC' },
    },

    magenta: {
      400: { value: '#D4479F' },
      500: { value: '#C6379A' },
    },

    /**
     * Warm stone neutrals. The light end sits between off-white and pale stone;
     * the dark end is a neutral near-black with a warm undertone, never blue and
     * never pure black.
     */
    stone: {
      0: { value: '#FFFFFF' },
      25: { value: '#FCFCFB' },
      50: { value: '#FAF9F8' }, // canvas
      100: { value: '#F5F4F2' }, // subtle panel, hover
      150: { value: '#F0EEEC' }, // sunken, subtle border
      200: { value: '#E7E5E3' }, // default border
      300: { value: '#D6D3D0' }, // strong border
      400: { value: '#A8A29E' }, // disabled text, muted icon
      450: { value: '#8B857E' }, // subtle text on dark surfaces
      500: { value: '#78716C' }, // subtle text on light surfaces
      600: { value: '#57534E' }, // muted text
      700: { value: '#44403C' },
      800: { value: '#292524' },
      850: { value: '#242322' }, // dark hover
      875: { value: '#201F1D' }, // dark raised surface
      900: { value: '#1A1918' }, // dark card
      925: { value: '#131211' }, // dark subtle panel
      950: { value: '#0A0A09' }, // dark canvas, sidebar, header
      975: { value: '#070706' }, // dark sunken
      1000: { value: '#1C1A18' }, // primary text on light
      1050: { value: '#0F0E0D' }, // strong text on light
    },

    /** Overlay scrims, so no component invents an alpha value. */
    scrim: {
      light: { value: 'rgba(28, 26, 24, 0.44)' },
      dark: { value: 'rgba(0, 0, 0, 0.70)' },
      /** Warm charcoal fade for the authentication image panel. */
      panelFade: {
        value:
          'linear-gradient(to top, rgba(11,10,10,0.94) 0%, rgba(11,10,10,0.74) 40%, rgba(11,10,10,0.34) 72%, rgba(11,10,10,0.18) 100%)',
      },
    },
  },

  fonts: {
    /**
     * Inter, matching the reference's compact dashboard typography. One family
     * throughout; the system mono stack is used only for identifiers.
     */
    heading: {
      value: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
    body: {
      value: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
    mono: {
      value: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
    },
  },

  fontSizes: {
    '2xs': { value: '0.6875rem' }, // 11px — badges, nav group labels
    xs: { value: '0.75rem' }, // 12px — helper text, table headers, captions
    sm: { value: '0.8125rem' }, // 13px — body small, table cells
    md: { value: '0.875rem' }, // 14px — body, controls, nav
    lg: { value: '0.9375rem' }, // 15px — card titles
    xl: { value: '1rem' }, // 16px — section titles
    '2xl': { value: '1.5rem' }, // 24px — page titles
    '3xl': { value: '1.75rem' }, // 28px — metric values
    '4xl': { value: '2rem' }, // 32px — auth heading
  },

  fontWeights: {
    normal: { value: '400' },
    medium: { value: '500' },
    semibold: { value: '600' },
    bold: { value: '700' },
  },

  lineHeights: {
    none: { value: '1' },
    tight: { value: '1.2' },
    snug: { value: '1.4' },
    normal: { value: '1.55' },
  },

  letterSpacings: {
    tighter: { value: '-0.02em' },
    tight: { value: '-0.011em' },
    normal: { value: '0' },
    /** Nav group labels only. */
    wide: { value: '0.06em' },
  },

  spacing: {
    px: { value: '1px' },
    0.5: { value: '0.125rem' },
    1: { value: '0.25rem' },
    1.5: { value: '0.375rem' },
    2: { value: '0.5rem' },
    2.5: { value: '0.625rem' },
    3: { value: '0.75rem' },
    3.5: { value: '0.875rem' },
    4: { value: '1rem' },
    5: { value: '1.25rem' },
    6: { value: '1.5rem' },
    8: { value: '2rem' },
    10: { value: '2.5rem' },
    12: { value: '3rem' },
    16: { value: '4rem' },
  },

  /**
   * Mirrors the reference's scale, which derives from a 0.625rem base:
   * controls, inputs and nav items take `md`, cards take `lg`, dialogs `xl`.
   */
  radii: {
    xs: { value: '4px' },
    sm: { value: '6px' },
    md: { value: '8px' },
    lg: { value: '14px' },
    xl: { value: '16px' },
    full: { value: '9999px' },
  },

  /**
   * Three rungs: cards barely lift, floating surfaces read clearly, dialogs
   * command the screen. Warm-neutral undertone.
   */
  shadows: {
    cardLight: { value: '0 1px 2px rgba(28, 25, 23, 0.04)' },
    floatingLight: {
      value: '0 8px 24px rgba(28, 25, 23, 0.10), 0 2px 6px rgba(28, 25, 23, 0.05)',
    },
    dialogLight: {
      value: '0 24px 56px rgba(28, 25, 23, 0.18), 0 4px 12px rgba(28, 25, 23, 0.08)',
    },
    stickyLight: { value: '0 1px 0 rgba(28, 25, 23, 0.05)' },
    cardDark: { value: '0 1px 2px rgba(0, 0, 0, 0.4)' },
    floatingDark: { value: '0 8px 26px rgba(0, 0, 0, 0.6), 0 2px 6px rgba(0, 0, 0, 0.4)' },
    dialogDark: { value: '0 24px 60px rgba(0, 0, 0, 0.72), 0 4px 12px rgba(0, 0, 0, 0.5)' },
    stickyDark: { value: '0 1px 0 rgba(0, 0, 0, 0.5)' },
  },

  sizes: {
    /** Buttons, inputs, selects, icon buttons. Reference sits around 36px. */
    control: {
      xs: { value: '1.5rem' }, // 24px
      sm: { value: '2rem' }, // 32px
      md: { value: '2.25rem' }, // 36px — default
      lg: { value: '2.5rem' }, // 40px
    },
    icon: {
      xs: { value: '0.75rem' }, // 12px
      sm: { value: '0.875rem' }, // 14px
      md: { value: '1.0625rem' }, // 17px — nav, topbar
      lg: { value: '1.25rem' }, // 20px — empty states
    },
    row: {
      sm: { value: '2.75rem' }, // 44px — dense table row
      md: { value: '3.25rem' }, // 52px — table row
      header: { value: '2.5rem' }, // 40px — table header
    },
    nav: { value: '2.375rem' }, // 38px — sidebar item
    sidebar: {
      expanded: { value: '16rem' }, // 256px
      collapsed: { value: '3rem' }, // 48px
    },
    topbar: { value: '3.5rem' }, // 56px
    container: {
      content: { value: '96rem' },
      form: { value: '40rem' },
      prose: { value: '44rem' },
    },
  },

  durations: {
    fast: { value: '120ms' },
    normal: { value: '180ms' },
  },

  easings: {
    standard: { value: 'cubic-bezier(0.2, 0, 0.2, 1)' },
  },

  /**
   * These names shadow Chakra's own z-index tokens, which its overlay recipes
   * reference — so they must stay on Chakra's magnitude scale. A small custom
   * scale (10, 20, 30…) silently sinks every backdrop below anything Chakra
   * positions natively, and menus (`popover`) end up painting over dialogs.
   *
   * Order: chrome < backdrop < dialog < menu < toast < tooltip. Menus sit above
   * dialogs deliberately, so a select inside a modal still opens on top.
   */
  zIndex: {
    base: { value: '0' },
    docked: { value: '10' },
    sticky: { value: '1100' }, // sticky table headers, skip link anchor
    sidebar: { value: '1150' },
    topbar: { value: '1200' },
    overlay: { value: '1300' }, // dialog and drawer backdrops
    dialog: { value: '1400' }, // matches Chakra's `modal`
    popover: { value: '1500' }, // menus, selects, popovers
    skipNav: { value: '1600' },
    toast: { value: '1700' },
    tooltip: { value: '1800' },
  },
}

export const breakpoints = {
  sm: '30rem',
  md: '48rem',
  lg: '64rem',
  xl: '80rem',
  '2xl': '96rem',
}
